if (!customElements.get('collection-page-toolbar')) {
  customElements.define(
    'collection-page-toolbar',
    class CollectionPageToolbar extends HTMLElement {
      connectedCallback() {
        this.keywordInput = this.querySelector('[data-collection-keyword]');
        this.searchButton = this.querySelector('[data-collection-search-button]');
        this.collectionSwitcher = this.querySelector('[data-collection-switcher]');
        this.sortSelect = this.querySelector('.facet-filters__sort');
        this.productGrid = this.querySelector('#product-grid');
        this.gridContainer = this.querySelector('#ProductGridContainer');
        this.emptyMessage = this.querySelector('[data-search-empty]');
        this.productCount = this.querySelector('#ProductCount');
        this.collectionHandle = this.dataset.collectionHandle;
        this.collectionUrl = this.dataset.collectionUrl;
        this.totalProductsCount = Number(this.dataset.productsCount || 0);
        this.originalGridNodes = this.productGrid ? Array.from(this.productGrid.children).map((node) => node.cloneNode(true)) : [];
        this.originalCountMarkup = this.productCount ? this.productCount.innerHTML : '';
        this.collectionProductsPromise = null;

        this.bindEvents();
      }

      bindEvents() {
        if (this.searchButton && this.keywordInput) {
          this.searchButton.addEventListener('click', () => this.handleKeywordSearch());
          this.keywordInput.addEventListener('keydown', (event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              this.handleKeywordSearch();
            }
          });
          this.keywordInput.addEventListener('input', () => {
            if (!this.keywordInput.value.trim()) this.restoreDefaultGrid();
          });
        }

        if (this.collectionSwitcher) {
          this.collectionSwitcher.addEventListener('change', (event) => {
            if (event.target.value) window.location.href = event.target.value;
          });
        }

        if (this.sortSelect) {
          this.sortSelect.addEventListener('change', () => {
            if (this.keywordInput && this.keywordInput.value.trim()) {
              this.handleKeywordSearch();
            }
          });
        }
      }

      async handleKeywordSearch() {
        const keyword = this.keywordInput ? this.keywordInput.value.trim().toLowerCase() : '';
        if (!keyword) {
          this.restoreDefaultGrid();
          return;
        }

        this.setLoading(true);

        try {
          const products = await this.fetchAllCollectionProducts();
          const filteredProducts = this.sortProducts(
            products.filter((product) => {
              const title = (product.title || '').toLowerCase();
              return title.includes(keyword);
            })
          );

          this.renderSearchResults(filteredProducts, keyword);
        } catch (error) {
          console.error('Collection keyword search failed', error);
          this.renderSearchResults([], keyword);
        } finally {
          this.setLoading(false);
        }
      }

      async fetchAllCollectionProducts() {
        if (this.collectionProductsPromise) return this.collectionProductsPromise;

        const limit = 250;
        this.collectionProductsPromise = (async () => {
          const products = [];
          let page = 1;

          while (true) {
            const response = await fetch(
              `${this.collectionUrl}/products.json?limit=${limit}&page=${page}`,
              { credentials: 'same-origin' }
            );

            if (!response.ok) {
              throw new Error(`Failed to fetch collection products: ${response.status}`);
            }

            const data = await response.json();
            const pageProducts = data.products || [];
            products.push(...pageProducts);

            if (pageProducts.length < limit) break;
            page += 1;
          }

          return products;
        })();

        return this.collectionProductsPromise;
      }

      sortProducts(products) {
        const sortBy = this.sortSelect ? this.sortSelect.value : 'manual';
        const sortedProducts = [...products];

        switch (sortBy) {
          case 'title-ascending':
            sortedProducts.sort((a, b) => (a.title || '').localeCompare(b.title || '', 'ja'));
            break;
          case 'title-descending':
            sortedProducts.sort((a, b) => (b.title || '').localeCompare(a.title || '', 'ja'));
            break;
          case 'price-ascending':
            sortedProducts.sort((a, b) => this.getProductPrice(a) - this.getProductPrice(b));
            break;
          case 'price-descending':
            sortedProducts.sort((a, b) => this.getProductPrice(b) - this.getProductPrice(a));
            break;
          case 'created-ascending':
            sortedProducts.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
            break;
          case 'created-descending':
          default:
            sortedProducts.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
            break;
        }

        return sortedProducts;
      }

      renderSearchResults(products, keyword) {
        if (!this.productGrid || !this.productCount) return;
        this.replaceGridItems(products);

        this.productCount.textContent = `「${keyword}」の検索結果 ${products.length}件`;

        if (this.emptyMessage) {
          this.emptyMessage.hidden = products.length !== 0;
        }
      }

      restoreDefaultGrid() {
        if (!this.productGrid || !this.productCount) return;

        this.productGrid.replaceChildren(...this.originalGridNodes.map((node) => node.cloneNode(true)));
        this.productCount.innerHTML = this.originalCountMarkup;
        this.emptyMessage = this.querySelector('[data-search-empty]');
        if (this.emptyMessage) this.emptyMessage.hidden = true;
      }

      replaceGridItems(products) {
        const fragment = document.createDocumentFragment();

        products.forEach((product) => {
          const wrapper = document.createElement('div');
          wrapper.innerHTML = this.renderProductCard(product).trim();
          if (wrapper.firstElementChild) {
            fragment.appendChild(wrapper.firstElementChild);
          }
        });

        this.productGrid.replaceChildren(fragment);
      }

      renderProductCard(product) {
        const variant = this.getPrimaryVariant(product);
        const isSoldOut = !variant || !variant.available;
        const price = this.formatMoney(this.getProductPrice(product));
        const stockText = isSoldOut
          ? '売り切れ'
          : Number.isFinite(Number(variant.inventory_quantity))
            ? `在庫: ${variant.inventory_quantity}`
            : '在庫あり';
        const sku = variant && variant.sku ? this.escapeHtml(variant.sku) : '&nbsp;';
        const productUrl = `/products/${product.handle}`;
        const image = product.images && product.images.length ? product.images[0] : null;
        const imageMarkup = image
          ? `<img src="${this.escapeAttribute(image.src)}" alt="${this.escapeAttribute(image.alt || product.title)}" loading="lazy" width="${image.width || 360}" height="${image.height || 500}">`
          : '<div class="placeholder-svg"></div>';
        const quantityValue = isSoldOut ? 0 : 1;

        return `
          <li class="grid__item collection-page__product-item">
            <article class="cv-collection-item-card collection-page__card">
              <div class="cv-collection-item-card__main collection-page__card-main">
                <a href="${this.escapeAttribute(productUrl)}" class="cv-collection-item-card__image-link" aria-label="${this.escapeAttribute(product.title)}">
                  <div class="cv-collection-item-card__image-wrap collection-page__card-image">
                    ${imageMarkup}
                  </div>
                </a>
                <div class="cv-collection-item-card__content">
                  <h3 class="cv-collection-item-card__title collection-page__card-title">
                    <a href="${this.escapeAttribute(productUrl)}">${this.escapeHtml(product.title)}</a>
                  </h3>
                  <p class="collection-page__card-meta">${sku}</p>
                  <p class="cv-collection-item-card__price"><span class="money"><span class="money__amount">${price}</span><span class="money__currency">円</span></span></p>
                  <p class="cv-collection-item-card__stock">${stockText}</p>
                </div>
              </div>
              <product-form class="product-form cv-collection-item-card__form" data-hide-errors="false">
                <div class="product-form__error-message-wrapper" role="alert" hidden>
                  <span class="product-form__error-message"></span>
                </div>
                <form method="post" action="/cart/add" accept-charset="UTF-8" class="form cv-collection-item-card__add-form" enctype="multipart/form-data" data-type="add-to-cart-form">
                  <input type="hidden" name="form_type" value="product">
                  <input type="hidden" name="utf8" value="✓">
                  <input type="hidden" name="id" value="${variant ? variant.id : ''}" ${isSoldOut ? 'disabled' : ''} class="product-variant-id">
                  <div class="cv-collection-item-card__purchase collection-page__card-purchase">
                    <div class="cv-collection-item-card__quantity-row">
                      <span class="cv-collection-item-card__quantity-label">個数</span>
                      <quantity-input class="quantity cv-collection-item-card__quantity">
                        <button class="quantity__button" name="minus" type="button"><span aria-hidden="true">-</span></button>
                        <input class="quantity__input" type="number" name="quantity" value="${quantityValue}" min="${quantityValue}" step="1" ${isSoldOut ? 'disabled' : ''}>
                        <button class="quantity__button" name="plus" type="button"><span aria-hidden="true">+</span></button>
                      </quantity-input>
                    </div>
                    <button type="submit" name="add" class="cv-collection-item-card__submit" ${isSoldOut ? 'disabled' : ''}>
                      <span>${isSoldOut ? '売り切れ' : 'カートに追加'}</span>
                    </button>
                  </div>
                </form>
              </product-form>
            </article>
          </li>
        `;
      }

      getPrimaryVariant(product) {
        return product.variants && product.variants.length ? product.variants[0] : null;
      }

      getProductPrice(product) {
        const variant = this.getPrimaryVariant(product);
        const rawPrice = variant?.price ?? product.price ?? 0;
        const numericPrice = Number(rawPrice);
        if (String(rawPrice).includes('.')) return numericPrice;
        return numericPrice;
      }

      formatMoney(price) {
        return Math.round(price).toLocaleString('ja-JP');
      }

      setLoading(isLoading) {
        const collectionBody = this.querySelector('.collection-page__products');
        if (!collectionBody) return;
        collectionBody.classList.toggle('loading', isLoading);
      }

      escapeHtml(value) {
        return String(value || '')
          .replaceAll('&', '&amp;')
          .replaceAll('<', '&lt;')
          .replaceAll('>', '&gt;')
          .replaceAll('"', '&quot;')
          .replaceAll("'", '&#39;');
      }

      escapeAttribute(value) {
        return this.escapeHtml(value);
      }
    }
  );
}
